import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ApiError } from '@/features/auth/api';
import { useRefreshList } from './useRefreshList';
import type {
  InvoiceSubject,
  InvoiceSummary,
  KsefInvoice,
  StoredInvoiceDetail,
  SyncStatus,
} from './types';

export const INVOICES_QUERY_KEY = ['invoices'] as const;

/** Kept separate from INVOICES_QUERY_KEY so the manual refresh button doesn't refetch the schedule. */
export const SYNC_STATUS_QUERY_KEY = ['invoice-sync-status'] as const;

/** Page size for the scrolling list — matches the API default; most months fit in one page. */
export const INVOICES_PAGE_SIZE = 50;

/** One page of the invoice list, mirroring the API's `PagedInvoices`. */
interface InvoicePage {
  items: KsefInvoice[];
  nextOffset: number | null;
}

async function fetchInvoicePage(
  subject: InvoiceSubject,
  month: string,
  offset: number,
): Promise<InvoicePage> {
  const params = new URLSearchParams({
    subject,
    month,
    limit: String(INVOICES_PAGE_SIZE),
    offset: String(offset),
  });
  const res = await fetch(`/api/v1/invoices?${params.toString()}`, { credentials: 'same-origin' });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ApiError(res.status, body || res.statusText);
  }
  return (await res.json()) as InvoicePage;
}

/**
 * Invoices for a subject in a given month ("yyyy-MM"), fetched a page at a time as the list scrolls.
 * Call `fetchNextPage` to pull the next page; `hasNextPage` is false once the server returns a null
 * `nextOffset`. Flatten `data.pages` to render — see `InvoiceListPage` (it also dedupes by KSeF
 * number, so a row never appears twice even if a page boundary overlaps).
 */
export function useInvoices(subject: InvoiceSubject, month: string) {
  return useInfiniteQuery({
    queryKey: [...INVOICES_QUERY_KEY, subject, month],
    queryFn: ({ pageParam }) => fetchInvoicePage(subject, month, pageParam),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextOffset,
    staleTime: 60_000,
    // Keep the previous month/subject on screen while the next one loads, so switching months or
    // views fades (blurred placeholder, see `isPlaceholderData`) instead of collapsing to a skeleton.
    placeholderData: keepPreviousData,
    // A 400 means "no certificate yet" — a user state, not a transient failure, so don't retry.
    retry: false,
  });
}

async function fetchInvoiceSummary(month: string): Promise<InvoiceSummary> {
  const params = new URLSearchParams({ month });
  const res = await fetch(`/api/v1/invoices/summary?${params.toString()}`, {
    credentials: 'same-origin',
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ApiError(res.status, body || res.statusText);
  }
  return (await res.json()) as InvoiceSummary;
}

/** Aggregated month stats for the Summary page ("yyyy-MM"). */
export function useInvoiceSummary(month: string) {
  return useQuery<InvoiceSummary>({
    queryKey: [...INVOICES_QUERY_KEY, 'summary', month],
    queryFn: () => fetchInvoiceSummary(month),
    staleTime: 60_000,
    // Same blur-while-loading treatment as the lists when switching months.
    placeholderData: keepPreviousData,
    retry: false,
  });
}

/**
 * Drops the cached invoice data and refetches what's on screen from the backend. The API serves
 * from its own DB (kept current by the background KSeF sync), so this pulls the latest stored data
 * rather than triggering a new KSeF fetch. `isFetching` tracks any in-flight invoice request so the
 * trigger can show progress.
 *
 * Before refetching, each loaded list is collapsed back to its first page: an infinite query would
 * otherwise re-fetch *every* page the user had scrolled through, so a refresh becomes "back to the
 * top, page 1". Non-infinite invoice queries (summary, detail) have no `pages` and pass through.
 *
 * The sync schedule is refreshed alongside the data (`also`), so the next-sync countdown stays in step.
 */
export function useRefreshInvoices() {
  return useRefreshList(INVOICES_QUERY_KEY, { also: [SYNC_STATUS_QUERY_KEY] });
}

async function fetchSyncStatus(): Promise<SyncStatus> {
  const res = await fetch('/api/v1/invoices/sync-status', { credentials: 'same-origin' });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ApiError(res.status, body || res.statusText);
  }
  return (await res.json()) as SyncStatus;
}

/**
 * When the next background sync pass is due. The schedule is global (cron-based), so this is the same
 * for every user; the indicator counts down to it and refreshes the on-screen data shortly after.
 */
export function useNextSync() {
  return useQuery<SyncStatus>({
    queryKey: SYNC_STATUS_QUERY_KEY,
    queryFn: fetchSyncStatus,
    staleTime: 60_000,
    retry: false,
  });
}

async function fetchInvoiceDetail(ksefNumber: string): Promise<StoredInvoiceDetail> {
  const res = await fetch(`/api/v1/invoices/${encodeURIComponent(ksefNumber)}`, {
    credentials: 'same-origin',
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ApiError(res.status, body || res.statusText);
  }
  return (await res.json()) as StoredInvoiceDetail;
}

/** Fetch a single invoice's metadata + parsed line items. Disabled until [ksefNumber] is set. */
export function useInvoiceDetail(ksefNumber: string | null) {
  return useQuery<StoredInvoiceDetail>({
    queryKey: [...INVOICES_QUERY_KEY, 'detail', ksefNumber],
    queryFn: () => fetchInvoiceDetail(ksefNumber!),
    enabled: ksefNumber != null,
    staleTime: 60_000,
  });
}

/** URL of an invoice's original FA(2) XML (cookie-authenticated, same-origin) — for download. */
export function invoiceXmlUrl(ksefNumber: string): string {
  return `/api/v1/invoices/${encodeURIComponent(ksefNumber)}/xml`;
}

/**
 * Fetches the KSeF "KOD I" verification URL for an invoice, built on demand by the backend (it hashes
 * the XML). Best-effort: returns null on any failure so PDF generation can proceed without the QR.
 */
export async function fetchInvoiceVerificationUrl(ksefNumber: string): Promise<string | null> {
  try {
    const res = await fetch(
      `/api/v1/invoices/${encodeURIComponent(ksefNumber)}/verification-url`,
      { credentials: 'same-origin' },
    );
    if (!res.ok) return null;
    const body = (await res.json()) as { url: string | null };
    return body.url ?? null;
  } catch {
    return null;
  }
}
